import { useState, useEffect, useContext } from "react";
import { Panel } from "primereact/panel";
import "./Dashboard.css";
import TopBar from "../TopBar/TopBar";
import { Menu } from "primereact/menu";
import { Link, ScrollRestoration, useLocation } from "react-router-dom";
import { ROUTE_ALLOWED_ROLES } from "../constants";
import { useVersion, useClientList } from "../hooks/queries";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
//import ProfilePicture from "./ProfilePicture";
import { ProgressSpinner } from "primereact/progressspinner"; // Importa el spinner de PrimeReact
import { classNames } from "primereact/utils";
import { NotificationBadgeContext } from "../NotificationBadgeContext";

/**
 * Main dashboard box
 * @param {object} props
 * @param {string} props.headerTitle
 * @param {string | undefined} props.backButtonUrl
 * @param {string | undefined} props.fixedWidth
 * @param {boolean?} props.dynamicGoBack
 * @param {boolean?} props.withPannel
 * @param {React.ReactNode} props.children
 */
export default function Dashboard({
  headerTitle,
  backButtonUrl,
  fixedWidth,
  children,
  panelClassName,
  dynamicGoBack = false,
  withPannel = true,
}) {
  const location = useLocation();

  // const staleCases = useStaleMedicalCases({ page: 1, filters: {} });

  /**
   * Renders an item in the sidebar
   * @param {object} item
   * @param {string} item.label
   * @param {string} item.icon
   * @param {string} item.url
   * @param {string[]} item.user
   * @param {RegExp[] | undefined} item.active
   */
  const ItemRenderer = (item) => {
    const [notifications] = useContext(NotificationBadgeContext);

    const notificationCount = notifications
      ? Object.values(notifications).reduce((prev, curr) => prev + curr, 0)
      : 0;

    let activeClassName = "";
    if (
      location.pathname === item.url ||
      (item.active &&
        item.active.some((regex) => regex.test(location.pathname)))
    ) {
      activeClassName = "active";
    }

    return (
      <div className={`p-menuitem-content ${activeClassName}`}>
        <Link to={item.url} className="flex align-items-center p-menuitem-link">
          <i className={item.icon} aria-hidden="true" />
          <span className="mx-2">{item.label}</span>
          {item.url === "/chat" && notificationCount > 0 && (
            <span
              className="border-circle text-white text-sm bg-cyan-600 px-2 py-1"
              style={{
                minWidth: 20,
                textAlign: "center",
                display: "inline-block",
              }}
            >
              {notificationCount}
            </span>
          )}
        </Link>
      </div>
    );
  };
  // Rutas disponibles del dashboard con sus permisos
  const items = [
    {
      label: "Crear Nuevo",
      items: [
        {
          label: "Crear caso",
          icon: "pi pi-plus",
          url: "/casos/nuevo",
          user: ROUTE_ALLOWED_ROLES["/casos/nuevo"],
          active: [/\/casos\/nuevo/],
        },
        {
          label: "Carga masiva",
          icon: "pi pi-upload",
          url: "/casos/carga-masiva",
          user: ROUTE_ALLOWED_ROLES["/casos/carga-masiva"],
          active: [/\/casos\/carga-masiva/],
          clients: ["PDVSA", "CVG Laboratorio"], // Restrict to specific client
        },
      ],
    },
    {
      label: "Casos Médicos",
      items: [
        {
          label: "Listado de casos",
          icon: "pi pi-list-check",
          url: "/casos",
          user: ROUTE_ALLOWED_ROLES["/casos"],
          active: [/\/casos\/\d+/],
        },
        {
          label: "Casos anulados",
          icon: "pi pi-list-check",
          url: "/casos/anulados",
          user: ROUTE_ALLOWED_ROLES["/casos/anulados"],
          active: [/\/casos\/anulados\/\d+/],
        },
        {
          label: "Casos por suscribir",
          icon: "pi pi-list-check",
          url: "/casos/por-suscribir",
          user: ROUTE_ALLOWED_ROLES["/casos/por-suscribir"],
          active: [/\/casos\/por-suscribir\/\d+/],
        },
        // {
        //   label: "Casos pendientes",
        //   icon: "pi pi-list-check",
        //   url: "/casos-pendientes",
        //   user: ROUTE_ALLOWED_ROLES["/casos-pendientes"],
        //   active: [/\/casos-pendientes\/\d+/],
        // },
        {
          label: "Casos reasignados",
          icon: "pi pi-list-check",
          url: "/casos/reasignados",
          user: ROUTE_ALLOWED_ROLES["/casos/reasignados"],
          active: [/\/casos\/reasignados\/\d+/],
        },
        {
          label: "Solicitudes pendientes",
          icon: "pi pi-list-check",
          url: "/casos/solicitudes-pendientes",
          user: ROUTE_ALLOWED_ROLES["/casos/solicitudes-pendientes"],
          active: [/\/casos\/solicitudes-pendientes\/\d+/],
        }
      ],
    },
    {
      label: "Médicos",
      items: [
        {
          label: "Médicos a bordo",
          icon: "pi pi-users",
          url: "/medicos",
          user: ROUTE_ALLOWED_ROLES["/medicos"],
        },
      ],
    },
    {
      label: "Anticipos",
      items: [
        {
          label: "Crear anticipo",
          icon: "pi pi-plus",
          url: "/anticipos/nuevo",
          user: ROUTE_ALLOWED_ROLES["/anticipos/nuevo"],
        },
        {
          label: "Anticipos",
          icon: "pi pi-wallet",
          url: "/anticipos",
          user: ROUTE_ALLOWED_ROLES["/anticipos"],
          active: [/\/anticipos\/proveedores\/\d+/],
        },
      ],
    },
    {
      label: "Fondo Incurrido",
      items: [
        {
          label: "Crear pago",
          icon: "pi pi-plus",
          url: "/fondo-incurrido/crear-pago",
          user: ROUTE_ALLOWED_ROLES["/fondo-incurrido/crear-pago"],
        },
        {
          label: "Abonos",
          icon: "pi pi-wallet",
          url: "/fondo-incurrido/abonos",
          user: ROUTE_ALLOWED_ROLES["/fondo-incurrido/abonos"],
          active: [/\/fondo-incurrido\/abonos\/\d+/],
        },
      ],
    },
    {
      label: "Baremos",
      items: [
        {
          label: "Baremos",
          icon: "pi pi-percentage",
          url: "/baremos",
          user: ROUTE_ALLOWED_ROLES["/baremos"],
          active: [/\/baremos\/\d+/],
        },
        {
          label: "Requerimientos médicos",
          icon: "pi pi-file-edit",
          url: "/requerimientos",
          user: ROUTE_ALLOWED_ROLES["/requerimientos"],
        },
      ],
    },
    {
      label: "Saldo",
      items: [
        {
          label: "Saldo disponible",
          icon: "pi pi-wallet",
          url: "/saldo",
          user: ROUTE_ALLOWED_ROLES["/saldo"],
          active: [/\/saldo\/\d+/],
        },
      ],
    },
    {
      label: "Pagos",
      items: [
        {
          label: "Pagos",
          icon: "pi pi-credit-card",
          url: "/pagos/clientes",
          user: ROUTE_ALLOWED_ROLES["/pagos/clientes"],
        },
      ],
    },
    {
      label: "API",
      items: [
        {
          label: "Descargar TOKEN",
          icon: "pi pi-download",
          url: "/api/generar-token",
          user: ROUTE_ALLOWED_ROLES["/api/generar-token"],
        },
      ],
    },
    {
      label: "Proveedores",
      items: [
        {
          label: "Listado de Proveedores",
          icon: "pi pi-building-columns",
          url: "/proveedores",
          user: ROUTE_ALLOWED_ROLES["/proveedores"],
          active: [/\/proveedores\/nuevo/, /\/proveedores\/editar\/\d+/],
        },
      ],
    },
    // {
    //   label: "Facturas",
    //   items: [
    //     {
    //       label: "Cargar factura",
    //       icon: "pi pi-plus",
    //       url: "/centro-medico/facturas/nueva",
    //       user: ROUTE_ALLOWED_ROLES["/centro-medico/facturas/nueva"],
    //     },
    //     {
    //       label: "Listado de facturas",
    //       icon: "pi pi-receipt",
    //       url: "/centro-medico/facturas",
    //       user: ROUTE_ALLOWED_ROLES["/centro-medico/facturas"],
    //     },
    //   ],
    // },

    {
      label: "Usuarios",
      items: [
        {
          label: "Listado de usuarios",
          icon: "pi pi-users",
          url: "/usuarios",
          user: ROUTE_ALLOWED_ROLES["/usuarios"],
        },
        {
          label: "Usuarios cliente",
          icon: "pi pi-users",
          url: "/usuarios/cliente",
          user: ROUTE_ALLOWED_ROLES["/usuarios/cliente"],
        },
        {
          label: "Asegurados",
          icon: "pi pi-users",
          url: "/asegurados",
          user: ROUTE_ALLOWED_ROLES["/asegurados"],
        },
      ],
    },
    {
      label: "RMS",
      items: [
        {
          label: "Pacientes RMS",
          icon: "pi pi-list-check",
          url: "/rms",
          user: ROUTE_ALLOWED_ROLES["/rms"],
          active: [/\/rms\/\d+/],
        },
      ],
    },
    {
      label: "Reportes",
      items: [
        {
          label: "Casos médicos",
          icon: "pi pi-list-check",
          url: "/reportes/casos-medicos",
          user: ROUTE_ALLOWED_ROLES["/reportes/casos-medicos"],
        },
      ],
    },
    {
      label: "Chat",
      items: [
        {
          label: "Chat",
          icon: "pi pi-comments",
          url: "/chat",
          user: ROUTE_ALLOWED_ROLES["/chat"],
        },
      ],
    },
    {
      label: "Cerrar sesión",
      items: [
        {
          label: "Cerrar sesión",
          icon: "pi pi-sign-out",
          url: "/logout",
          user: ROUTE_ALLOWED_ROLES["/logout"],
        },
      ],
    },
  ];

  const itemsFilter = (item) => {
    const role = localStorage.getItem("role");
    const isAdmin = localStorage.getItem("isAdmin");
    const userClients = clientListQuery.data;

    if (!role) return false;

    if (isAdmin === "true") {
      return true;
    }

    if (userClients && userClients.length > 0) {
      // Check if the item is allowed for the user's role and assigned clients
      return (
        item.user.includes(role) &&
        (!item.clients ||
          item.clients.some((client) =>
            userClients.some((userClient) => userClient.name === client)
          ))
      );
    }

    return item.user.includes(role);
  };
  const [visible, setVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isActive, setIsActive] = useState(!isMobile);
  const clientListQuery = useClientList();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const userName = localStorage.getItem("username");

  const version = process.env.REACT_APP_VERSION;
  const useVersionQuery = useVersion();
  // const [showModalVersion, setShowModalVersion] = useState(true);

  return (
    <div className="layout-dashboard">
      <div className={isActive ? "layout-sidebar " : "sidebar-hidden"}>
        <div className="mb-1">
          {/* <ProfilePicture />*/}
          <p className="text-lg mt-1 mb-1 text-blue-600 font-medium bg-gray-100 px-1 py-1 rounded">
            {userName || ""}
          </p>
        </div>
        {clientListQuery.isLoading ? ( // Mostrar spinner mientras carga
          <div className="loading-container">
            <ProgressSpinner />
          </div>
        ) : (
          <Menu
            model={items
              .map((routeGroup) => ({
                ...routeGroup,
                items: routeGroup.items
                  .filter(itemsFilter)
                  .map((item) => ({ ...item, template: ItemRenderer })),
              }))
              .filter((routeGroup) => !!routeGroup.items.length)}
          />
        )}
      </div>

      <div
        className={
          isActive ? "layout-content" : "layout-content layout-inactive"
        }
      >
        <TopBar
          setIsActive={() => setIsActive(!isActive)}
          setIsActiveMobile={() => setVisible(!visible)}
          headerTitle={headerTitle}
        ></TopBar>
        <div
          className={classNames("layout-main-container", {
            "p-0": location.pathname === "/chat",
          })}
        >
          <div className="layout-main">
            {backButtonUrl && (
              <Link to={backButtonUrl} className="back-link">
                <i className="pi pi-chevron-left" aria-hidden="true"></i>
                <span>Volver</span>
              </Link>
            )}
            {dynamicGoBack && (
              <span
                onClick={() => window.history.back()}
                id="backg"  className="back-link cursor-pointer"
              >
                <i className="pi pi-chevron-left" aria-hidden="true"></i>
                <span>Volver</span>
              </span>
            )}
            {withPannel ? (
              <Panel
                className={panelClassName ? panelClassName : "w-min md:w-12"}
                header={""}
                style={fixedWidth ? { width: fixedWidth } : {}}
              >
                {children}
              </Panel>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
      <ScrollRestoration />
        
      <Dialog
        header="Nueva Actualización"
        // visible={showModalVersion}
        visible={
          useVersionQuery.data && useVersionQuery.data?.version !== version
        }
        // onHide={() => useVersionQuery.refetch()}
        onHide={() => window.location.reload()}
        style={{ width: "50vw" }}
      >

        
        <div className="flex flex-column align-items-center justify-content-center">
          <p>
            Existe una nueva versión de esta página. Por favor, recargue para
            obtener la última versión.
          </p>
        </div>
        <div className="flex justify-content-end">
          <Button
            label="Recargar"
            icon="pi pi-refresh"
            onClick={() => window.location.reload()}
          />
          {/* <Button
            label="Cerrar"
            icon="pi pi-times"
            onClick={() => useVersionQuery.refetch()}
          /> */}
        </div>
      </Dialog>
    </div>
  );
}
